import dayjs from "dayjs";
import {
  ArrowLeft,
  Briefcase,
  Building,
  Calendar,
  Clock,
  Globe,
  IndianRupee,
  MapPin,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Navbar from "../../components/common/Navbar";
import { JobApplicationForm } from "../../components/job/application-form";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";
import {
  Job,
  JobExperienceLevelEnum,
  JobStatusEnum,
  JobTypeEnum,
} from "../../swagger";
import { candidateApi } from "../../utils/configuration";

export default function JobDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showApplicationForm, setShowApplicationForm] = useState(false);

  const jobId = params.id;

  // API call to fetch job by ID 
  const fetchJobById = useCallback(async (id: string) => {
    try {
      setIsLoading(true);
      const response = await candidateApi.candidateJobById({
        id: Number(id),
      });
      setJob(response);
    } catch (error) {
      console.error("Error fetching job:", error);
      await Swal.fire({
        icon: "error",
        title: "Job Not Found!",
        text: "The job you are looking for does not exist or has been removed.",
        confirmButtonColor: "#4f46e5",
      });
      navigate("/jobs"); // Redirect to jobs list
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (jobId) {
      fetchJobById(jobId);
    }
  }, [jobId, fetchJobById]);

  // Helper functions for display formatting
  const getJobTypeDisplay = (type?: JobTypeEnum) => {
    switch (type) {
      case JobTypeEnum.Fulltime:
        return "Full-time";
      case JobTypeEnum.Parttime:
        return "Part-time";
      case JobTypeEnum.Contract:
        return "Contract";
      case JobTypeEnum.Freelance:
        return "Freelance";
      case JobTypeEnum.Internship:
        return "Internship";
      default:
        return "Not specified";
    }
  };

  const getExperienceLevelDisplay = (level?: JobExperienceLevelEnum) => {
    switch (level) {
      case JobExperienceLevelEnum.Entry:
        return "Entry Level (0-2 years)";
      case JobExperienceLevelEnum.Mid:
        return "Mid Level (2-5 years)";
      case JobExperienceLevelEnum.Senior:
        return "Senior Level (5-8 years)";
      case JobExperienceLevelEnum.Lead:
        return "Lead Level (8+ years)";
      case JobExperienceLevelEnum.Executive:
        return "Executive Level";
      default:
        return "Not specified";
    }
  };

  const getStatusDisplay = (status?: JobStatusEnum) => {
    return status === JobStatusEnum.Published ? "Published" : "Draft";
  };

  const getStatusBadgeColor = (status?: JobStatusEnum) => {
    return status === JobStatusEnum.Published
      ? "bg-green-100 text-green-800"
      : "bg-yellow-100 text-yellow-800";
  };

  const formatSalary = (start?: number, end?: number) => {
    if (start && end) {
      return `₹${start.toLocaleString()} - ₹${end.toLocaleString()}`;
    }
    return "Not specified";
  };

  const formatDate = (date: Date) => {
    if (date) {
      return dayjs(date).format("MMM DD, YYYY");
    }
    return "Not specified";
  };

  const onFormClose = () => {
    setShowApplicationForm(false);
    navigate(`/jobs`);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading job details...</p>
        </div>
      </div>
    );
  }

  // Job not found state
  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Job Not Found
          </h1>
          <Button onClick={() => navigate("/jobs")}>Back to Jobs</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/jobs")}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Jobs
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-2xl mb-2">{job.title}</CardTitle>
                    <div className="flex items-center gap-4 text-gray-600 mb-4">
                      <div className="flex items-center gap-1">
                        <Building className="h-4 w-4" />
                        <span className="font-medium">{job.companyName}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        <span>{job.location}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary">
                        {getExperienceLevelDisplay(job.experienceLevel)}
                      </Badge>
                      <Badge variant="outline">
                        {getJobTypeDisplay(job.type)}
                      </Badge>
                      <Badge
                        className={`${getStatusBadgeColor(
                          job.status
                        )} border-0`}
                      >
                        {getStatusDisplay(job.status)}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {job.description && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3 flex items-center">
                      <Briefcase className="h-5 w-5 mr-2 text-indigo-600" />
                      Job Description
                    </h3>
                    <div className="prose max-w-none">
                      <div className="whitespace-pre-line text-gray-700 leading-relaxed">
                        {job.description}
                      </div>
                    </div>
                  </div>
                )}

                {job.requirements && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3">Requirements</h3>
                    <div className="prose max-w-none">
                      <div className="whitespace-pre-line text-gray-700 leading-relaxed">
                        {job.requirements}
                      </div>
                    </div>
                  </div>
                )}

                {job.benefits && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3">
                      Benefits & Perks
                    </h3>
                    <div className="prose max-w-none">
                      <div className="whitespace-pre-line text-gray-700 leading-relaxed">
                        {job.benefits}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Company Info */}
            {job.companyDesc && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle>About {job.companyName}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="whitespace-pre-line text-gray-700 leading-relaxed">
                    {job.companyDesc}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Apply Card - Only show if job is published */}
            {job.status === JobStatusEnum.Published && (
              <Card>
                <CardHeader>
                  <CardTitle>Apply for this position</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button
                    className="w-full bg-indigo-600 text-white hover:bg-indigo-700"
                    size="lg"
                    onClick={() => setShowApplicationForm(true)}
                  >
                    Apply Now
                  </Button>
                  <p className="text-sm text-gray-600 text-center">
                    Join {job.companyName} and make an impact
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Job Details */}
            <Card>
              <CardHeader>
                <CardTitle>Job Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <IndianRupee className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">Salary</span>
                  </div>
                  <span className="font-medium text-right">
                    {formatSalary(job.salaryStart, job.salaryEnd)}
                  </span>
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">Experience</span>
                  </div>
                  <span className="font-medium text-right">
                    {getExperienceLevelDisplay(job.experienceLevel)}
                  </span>
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">Job Type</span>
                  </div>
                  <span className="font-medium">
                    {getJobTypeDisplay(job.type)}
                  </span>
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">Location</span>
                  </div>
                  <span className="font-medium text-right">
                    {job.location || "Not specified"}
                  </span>
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">Posted</span>
                  </div>
                  <span className="font-medium">
                    {formatDate(job.createdAt!)}
                  </span>
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">Status</span>
                  </div>
                  <Badge
                    className={`${getStatusBadgeColor(job.status)} border-0`}
                  >
                    {getStatusDisplay(job.status)}
                  </Badge>
                </div>

                {/* Domain/Category Information if available */}
                {job.Domain && (
                  <>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-600">Domain</span>
                      </div>
                      <span className="font-medium text-right">
                        {job.Domain.title}
                      </span>
                    </div>
                  </>
                )}

                {job.Category && (
                  <>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-600">Category</span>
                      </div>
                      <span className="font-medium text-right">
                        {job.Category.title}
                      </span>
                    </div>
                  </>
                )}

                {job.SubCategory && (
                  <>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-600">
                          Subcategory
                        </span>
                      </div>
                      <span className="font-medium text-right">
                        {job.SubCategory.title}
                      </span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Additional Info Card */}
            <Card>
              <CardHeader>
                <CardTitle>Additional Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Job ID:</span>
                    <p className="text-gray-600">#{job.id}</p>
                  </div>
                  {job.updatedAt && (
                    <div>
                      <span className="font-medium text-gray-700">
                        Last Updated:
                      </span>
                      <p className="text-gray-600">
                        {formatDate(job.updatedAt!)}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Application Form Modal - Placeholder for future implementation */}
      {/* Application Form Modal */}
      {showApplicationForm && (
        <JobApplicationForm job={job} onClose={() => onFormClose()} />
      )}
    </div>
  );
}
